import { graphql, useFragment } from "react-relay";
import { UserMenuFragment$key } from "./__generated__/UserMenuFragment.graphql";
import { FormattedMessage } from "react-intl";
import { Dropdown } from "./Dropdown";
import LogoutButton from "./LogoutButton";
import EntityProfilePic from "./EntityProfilePic";
import { Link, LinkProps } from "react-router";
import {
  BellRinging03Icon,
  ChevronRightIcon,
  PuzzlePiece01Icon,
  User03Icon,
} from "@/icons";
import { cn } from "@/utils/tailwind";
import { Link as ButtonLink } from "@/kit/ui/link";

const Fragment = graphql`
  fragment UserMenuFragment on User {
    id
    displayName
    username
    ...EntityProfilePicFragment @arguments(thumbnail: true)
  }
`;

const UserMenuButton = ({ className, children, ...rest }: LinkProps) => {
  return (
    <Dropdown.BaseItem>
      <Link
        className={cn(
          "flex flex-row items-center space-x-4 p-2 hover:bg-grey-100 rounded-lg",
          className,
        )}
        {...rest}
      >
        <div className="flex flex-row items-center space-x-4 w-full">
          {children}
        </div>
        <ChevronRightIcon className="w-5 h-5 text-grey-600" />
      </Link>
    </Dropdown.BaseItem>
  );
};

const UserMenu = ({
  viewer: viewerFragment,
}: {
  viewer: UserMenuFragment$key;
}) => {
  const viewer = useFragment(Fragment, viewerFragment);
  return (
    <div className="lg:pt-0 pt-6 flex flex-col space-y-2">
      <UserMenuButton to={`/${viewer.username}`}>
        <EntityProfilePic entity={viewer} size="10" />
        <div>
          <div className="font-semibold">{viewer.displayName}</div>
          <div>@{viewer.username}</div>
        </div>
      </UserMenuButton>
      <UserMenuButton to={`/${viewer.username}/edit`}>
        <User03Icon className="w-5 h-5 text-grey-600" />
        <div>
          <FormattedMessage defaultMessage="Profile Settings" />
        </div>
      </UserMenuButton>
      <UserMenuButton to="settings/notifications">
        <BellRinging03Icon className="w-5 h-5 text-grey-600" />
        <div>
          <FormattedMessage defaultMessage="Notification Settings" />
        </div>
      </UserMenuButton>
      <UserMenuButton to="settings/applications">
        <PuzzlePiece01Icon className="w-5 h-5 text-grey-600" />
        <div>
          <FormattedMessage defaultMessage="Connected Apps" />
        </div>
      </UserMenuButton>
      <div className="pt-2">
        <LogoutButton />
      </div>
    </div>
  );
};

const UserMenuDropdown = ({
  viewer: viewerFragment,
}: {
  viewer: UserMenuFragment$key;
}) => {
  const viewer = useFragment(Fragment, viewerFragment);
  return (
    <Dropdown
      autoCloses
      trigger={
        <div className="flex flex-row items-center hover:cursor-pointer">
          <EntityProfilePic entity={viewer} size="10" />
        </div>
      }
    >
      <Dropdown.List>
        <UserMenu viewer={viewerFragment} />
      </Dropdown.List>
    </Dropdown>
  );
};

export const UserMenuNavBar = ({
  viewer,
}: {
  viewer?: UserMenuFragment$key | null;
}) => {
  if (viewer) {
    return <UserMenuDropdown viewer={viewer} />;
  }
  return (
    <>
      <ButtonLink variant="secondary" size="md" to="/login" rel="nofollow">
        <FormattedMessage defaultMessage="Log in" />
      </ButtonLink>
      <ButtonLink variant="primary" size="md" to="/signup">
        <FormattedMessage defaultMessage="Sign up" />
      </ButtonLink>
    </>
  );
};

export const UserMenuDrawer = ({
  viewer: viewer,
}: {
  viewer?: UserMenuFragment$key | null;
}) => {
  if (viewer) {
    return <UserMenu viewer={viewer} />;
  }
  return (
    <div className="flex flex-col space-y-2">
      <ButtonLink variant="primary" size="md" to="/signup">
        <FormattedMessage defaultMessage="Sign up" />
      </ButtonLink>
      <ButtonLink variant="secondary" size="md" to="/login" rel="nofollow">
        <FormattedMessage defaultMessage="Log in" />
      </ButtonLink>
    </div>
  );
};
